import { InputField } from 'lib/iterable-hook-form/FormField';
import { ErrorBoundary } from 'modules/error';
import { useExperimentsTranslations } from 'modules/experiments/hooks';
import { getCampaignInfoState } from 'modules/experiments/takeover/ducks/selectors';
import {
  useExperimentCreation,
  usePrivateExperimentTakeover,
} from 'modules/experiments/takeover/hooks';
import { CREATE_EXPERIMENT_SCHEMA } from 'modules/experiments/takeover/schemas/createExperiment';
import { EXPERIMENT_CREATION_FORM_INPUT } from 'modules/experiments/takeover/types';
import { ExperimentType } from 'modules/graphql';
import { FunctionComponent, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { TFunction } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Product, Team } from 'teams';
import { BannerVariants } from 'ui/banner/types';
import { BUTTON_SIZES, Button } from 'ui/button';
import { FormField } from 'ui/formField';
import { InfoFill16, NegativeFillDiamond } from 'ui/icons';
import { MODAL_SIZES, MODAL_VARIANTS, Modal, ModalBody, ModalHeader } from 'ui/modal';
import { Option } from 'ui/radioList/types';
import {
  serializationOptionsForBooleanValue,
  useSearchParamState,
} from 'utils/hooks/useSearchParamState';
import { useExperimentCreationModalContext } from './ExperimentCreationModalContext';
import type { CreateExperimentFormSchemaType } from './ExperimentCreationModalProvider';
import {
  ExperimentTypeOptions,
  FormFieldWithTopPadding,
  LightningIcon,
  StyledBanner,
  StyledModalFooter,
} from './Styled';

type ExperimentCreationModalValues = {
  name: string;
  type: ExperimentType;
};

/**
 * Experiment Type Option RuleSet handles by backend
 * Subject line: email
 * Preheader text: email
 * From name and sender: email
 * Everything: always
 * Email body: email
 * Send time: Blast && Email,SMS,Push
 * Send time optimization: when STO is available as a Delivery option within campaign setup
 */

export const getDisplayedExperimentTypeOptions = (
  types: ExperimentType[],
  isCampaignSTOEnabled: boolean,
  t: TFunction,
): Option[] => {
  if (!types?.length) return [] as Option[];
  return types.map((expType, index) => {
    const Icon =
      expType === ExperimentType.SendTimeOptimization
        ? (LightningIcon as FunctionComponent)
        : undefined;

    /** We want to disable send time experiment option
     * when the campaign has STO delivery turned on
     */
    const isSendTimeExpForSTOOptimizedCampaign =
      isCampaignSTOEnabled && expType === ExperimentType.SendTime;
    const tooltipProps = isSendTimeExpForSTOOptimizedCampaign
      ? {
          isDisabled: isSendTimeExpForSTOOptimizedCampaign,
          isTooltipEnabled: isSendTimeExpForSTOOptimizedCampaign,
          tooltipContent: t('creationModal.disableSendTimeTooltip'),
        }
      : {};

    return {
      id: `option-${expType}-${index}`,
      name: t(`experimentType.${expType}`),
      value: expType as string,
      Icon,
      ...tooltipProps,
    };
  });
};

export const ExperimentCreationModal = () => {
  const { t } = useExperimentsTranslations();
  const {
    campaignInfo: { id, isCampaignSTOEnabled },
    openExperimentTakeoverAfterCreation,
    closeExperimentCreationModal,
  } = usePrivateExperimentTakeover();
  const { experimentTypes, isError } = useExperimentCreationModalContext();
  const [creatingExperiment, setCreatingExperiment] = useState(false);

  const { isCampaignValid, hasLocales } = useSelector(getCampaignInfoState);

  const [, setModalParam] = useSearchParamState({
    searchParamKey: 'isExperimentModalOpen',
    stateValueWhenSearchParamIsUnset: false,
    ...serializationOptionsForBooleanValue,
  });
  const clearUrlParam = () => setModalParam(false);

  // Experiment Creation
  const createExperiment = useExperimentCreation({
    campaignId: id,
    onSuccess: (experimentId) => {
      clearUrlParam();
      setCreatingExperiment(false);
      openExperimentTakeoverAfterCreation(experimentId);
    },
    onError: () => {
      clearUrlParam();
      setCreatingExperiment(false);
    },
  });

  const handleExperimentCreation = ({ name, type }: ExperimentCreationModalValues) => {
    setCreatingExperiment(true);
    createExperiment(name, type);
  };

  const handleOnClose = () => {
    clearUrlParam();
    closeExperimentCreationModal();
  };

  // Experiment Types
  const displayedExperimentTypeOptions = getDisplayedExperimentTypeOptions(
    experimentTypes,
    isCampaignSTOEnabled,
    t,
  );

  // Form State
  const {
    getValues,
    handleSubmit,
    formState: { errors },
  } = useFormContext<CreateExperimentFormSchemaType<typeof CREATE_EXPERIMENT_SCHEMA>>();

  const nameErrorMessage = errors?.name?.message;

  return (
    <Modal
      data-test="create-experiment-modal"
      id="create-experiment-modal"
      isModalOpen={true}
      onClose={handleOnClose}
      size={MODAL_SIZES.MEDIUM}
      variant={MODAL_VARIANTS.DEFAULT}
    >
      <ModalHeader>{t('button.createNew')}</ModalHeader>
      <ModalBody>
        <ErrorBoundary teamName={Team.EXPERIMENT_TEAM} productName={Product.EXPERIMENT_EDITOR}>
          {/* Error Message (conditional) */}
          {!isCampaignValid && (
            <StyledBanner Icon={<NegativeFillDiamond />} variant={BannerVariants.ERROR}>
              {t('creationModal.invalidCampaignMessage')}
            </StyledBanner>
          )}
          {/* Locale Info Message */}
          {hasLocales && (
            <StyledBanner Icon={<InfoFill16 />} variant={BannerVariants.INFO}>
              {t('creationModal.localeInfo')}
            </StyledBanner>
          )}
          {/* Name */}
          <FormField
            inputId="experiment-name-field"
            labelText={t('creationModal.nameLabel')}
            errorText={nameErrorMessage}
            hasError={!!nameErrorMessage}
          >
            <InputField autoFocus id="experiment-name" data-test="experiment-name" name="name" />
          </FormField>

          {/* Type */}
          <FormFieldWithTopPadding
            inputId="experiment-name-field"
            labelText={t('creationModal.typeLabel')}
            descriptionText={t('creationModal.typeFieldDescription')}
          >
            <ExperimentTypeOptions
              data-test="experiment-type-options"
              name={EXPERIMENT_CREATION_FORM_INPUT.TYPE} // RHF field
              options={displayedExperimentTypeOptions}
            />
          </FormFieldWithTopPadding>
        </ErrorBoundary>
      </ModalBody>
      <StyledModalFooter>
        <Button
          data-test={`confirm-experiment-creation-button`}
          isLoading={creatingExperiment}
          isDisabled={isError || !isCampaignValid}
          onClick={handleSubmit(() =>
            handleExperimentCreation(getValues() as ExperimentCreationModalValues),
          )}
          size={BUTTON_SIZES.LARGE}
        >
          {t('button.createExperiment')}
        </Button>
      </StyledModalFooter>
    </Modal>
  );
};
